<template>
    <div>
        <report-header
            :title="title"
            :isShowDateRange="true"
            :formatDateRangeTitle="dateRange"
        />
        <b-card id="report-card">
            <div id="report-body" class="w-100">
                <QTable
                    :data="results"
                    :columns="formattedColumns"
                    :hide-bottom="true"
                    :rows-per-page-options="[0]"
                    :loading="isLoading"
                    class="q-table"
                >
                    <template
                        v-slot:bottom-row
                        v-if="results.length == 0 && !isError && !isLoading"
                    >
                        <q-tr>
                            <q-td colspan="12">No records available</q-td>
                        </q-tr>
                    </template>
                    <template v-slot:body-cell-item_name="props">
                        <q-td :props="props">
                            <item-name
                                :item="{
                                    name: props.row.item_name,
                                    encodedId: props.row.item_encoded_id
                                }"
                            />
                        </q-td>
                    </template>
                </QTable>
            </div>
        </b-card>
    </div>
</template>
<script>
/**
 * @name InventoryLog
 * @description Inventory log report (99)
 */
import Report from "@/mixins/report.js";
import "@/css/quasar-table.css";
import { isEmpty } from "lodash";
import { QTable } from "quasar";
import ReportService from "@/services/report";
import CREPORT from "@/constants/report";
import ItemName from "@/components/commons/item-name";
import ItemServices from "@/services/items.js";

const REPORT_NAME = "Inventory Log";
export default {
    components: {
        QTable,
        ItemName,
        ReportHeader: () => import("@/components/report/report-header")
    },
    mixins: [Report],
    props: {
        data: {
            type: Object,
            default() {
                return {
                    start: this.getStartDate("day"),
                    end: this.getEndDate("day")
                };
            }
        }
    },
    data() {
        return {
            isLoading: false,
            results: [],
            title: REPORT_NAME,
            isError: false,
            dateRange: "",
            formattedColumns: [
                {
                    name: "item_name",
                    label: "Item Name",
                    field: "item_name",
                    align: "left",
                    style: "min-width: 150px"
                },
                {
                    name: "date",
                    label: "Date",
                    field: "formatted_update_at",
                    align: "left",
                    style: "white-space: nowrap",
                    classes: "text-capitalize"
                },
                {
                    name: "initial_base_price",
                    label: "Initial Base Price",
                    field: row => row.initial_base_price,
                    format: (val, row) => {
                        return row.initial_base_price == 0
                            ? this._toCurrency(row.initial_base_price)
                            : row.initial_base_price;
                    },
                    align: "left",
                    classes: "text-capitalize"
                },
                {
                    name: "update_base_price",
                    label: "Updated Base Price",
                    field: row => row.update_base_price,
                    format: (val, row) => {
                        return row.update_base_price == 0
                            ? this._toCurrency(row.update_base_price)
                            : row.update_base_price;
                    },
                    align: "left",
                    classes: "text-capitalize"
                },
                {
                    name: "initial_dose",
                    label: "Initial Base Quantity",
                    field: "initial_dose",
                    align: "left"
                },
                {
                    name: "update_dose",
                    label: "Updated Base Quantity",
                    field: "update_dose",
                    align: "left"
                },
                {
                    name: "initial_min_price",
                    label: "Initial Min Price",
                    field: row => row.initial_min_price,
                    format: (val, row) => {
                        return row.initial_min_price != null
                            ? this._toCurrency(row.initial_min_price)
                            : "";
                    },
                    align: "left",
                    style: "white-space: nowrap",
                    classes: "text-capitalize"
                },
                {
                    name: "update_min_price",
                    label: "Updated Min Price",
                    field: row => row.update_min_price,
                    format: (val, row) => {
                        return row.update_min_price != null
                            ? this._toCurrency(row.update_min_price)
                            : "";
                    },
                    align: "left",
                    style: "white-space: nowrap"
                },
                {
                    name: "initial_fee",
                    label: "Initial Fee",
                    field: "initial_fee",
                    align: "left",
                    classes: "text-capitalize"
                },
                {
                    name: "update_fee",
                    label: "Updated Fee",
                    field: "update_fee",
                    align: "left",
                    classes: "text-capitalize"
                },
                {
                    name: "initial_taxes",
                    label: "Initial Taxes",
                    field: "initial_taxes",
                    align: "left",
                    classes: "text-capitalize"
                },
                {
                    name: "update_taxes",
                    label: "Updated Taxes",
                    field: "update_taxes",
                    align: "left",
                    classes: "text-capitalize"
                },
                {
                    name: "initial_dosage",
                    label: "Initial Dosage",
                    field: "initial_dosage",
                    align: "left"
                },
                {
                    name: "update_dosage",
                    label: "Updated Dosage",
                    field: "update_dosage",
                    align: "left"
                },
                {
                    name: "initial_concentration",
                    label: "Initial Concentration",
                    field: "initial_concentration",
                    align: "left"
                },
                {
                    name: "update_concentration",
                    label: "Updated Concentration",
                    field: "update_concentration",
                    align: "left"
                },
                {
                    name: "initial_min_price_type",
                    label: "Initial Minimum Price Type",
                    field: "initial_min_price_type",
                    align: "left"
                },
                {
                    name: "update_min_price_type",
                    label: "Updated Minimum Price Type",
                    field: "update_min_price_type",
                    align: "left"
                },
                {
                    name: "update_by",
                    label: "Updated By",
                    field: "update_by",
                    align: "left"
                }
            ]
        };
    },
    async mounted() {
        let self = this;
        await self._getSetting();
        self.data.isShowDateRange = true;
        self.data.datePickerOptionDefault = 1;
        self.data.isShowItemClass = true;
        self.data.isShowItemCategory = true;
        self.data.isShowItemOptions = true;
        self.data.isShowItemStatus = true;
        self.data.start = this.getStartDate("week");
        self.data.end = this.getEndDate("day");
        self.data.from = this.getStartDate("week").unix();
        self.data.to = this.getEndDate("day").unix();
        self.dateFormat = self.getDateFormat();
        self.dynamicPageTitle(self.title);

        let query = self.$route.query;
        const itemsFromUrl = query.items ? JSON.parse(query.items) : [];
        const categoryFromUrl = query.category ? JSON.parse(query.category) : 0;
        const itemClassFromUrl =
            query.item_class !== null ? query.item_class : 0;
        const statusFromUrl = query.status !== null ? query.status : null;

        if (!isEmpty(itemsFromUrl)) {
            self.data.itemShowMode = 2;
            self.data.items = itemsFromUrl.map(itemId => {
                return {
                    raw_id: itemId,
                    name: itemId
                };
            });
            // Reupdate the self.data.items in background
            await self.getItemsById(itemsFromUrl);
        }

        self.data.category = categoryFromUrl;
        self.data.itemClass = itemClassFromUrl;
        self.data.status = statusFromUrl;
        if (!isEmpty(query.to) && !isEmpty(query.from)) {
            self._getSetting().then(() => {
                self.initSetDates().then(() => {
                    self.init();
                });
            });
        } else {
            self.init();
        }
        self.$root.$on("report-filter-date-range:refresh", self.prepareData);
        self.$root.$on("inventory-log:refresh", function () {
            self.refresh = 1;
            self.init();
        });
    },
    beforeDestroy: function () {
        this.data.datePickerOptionDefault = null;
        this.data.isShowItemClass = false;
        this.data.isShowItemCategory = false;
        this.data.isShowItemOptions = false;
        this.$root.$off("report-filter-date-range:refresh", this.prepareData);
    },
    methods: {
        async getItemsById(itemIds = []) {
            if (isEmpty(itemIds)) {
                return;
            }
            let self = this;
            const params = {
                item_ids: itemIds
            };
            const res = await ItemServices.getItemsWithSearch(params);

            if (res.message && res.message.code === 200) {
                // this endpoint returns results in an Object instead of array.
                // so we have to remove some keys and convert the object to array,
                // before we can pass to vue-select as dropdown options.
                let results = res;
                delete results.message;
                delete results.total;

                let options = Object.values(results);
                self.data.items = options;
            }
        },
        prepareData(params) {
            if (params) {
                this.data.itemClass = params.itemClass;
                this.data.from = params.from;
                this.data.to = params.to;
                this.data.items = params.items;
                this.data.category = params.category;
                this.refresh = 0;
            }
            this.init();
        },
        async initSetDates() {
            let query = this.$route.query;
            if (!isEmpty(query.to) && !isEmpty(query.from)) {
                this.data.datePickerOptionDefault = 1;
                this.data.start = moment.unix(query.from).tz(this._timeZone);
                this.data.end = moment.unix(query.to).tz(this._timeZone);
                this.data.from = query.from;
                this.data.to = query.to;
                this.dateRange = this.getDateRange(query.from, query.to);
            } else {
                this.data.start = this.getStartDate("isoWeek");
                this.data.end = this.getEndDate("day");
                this.data.from = this.converToUnix(this.data.start);
                this.data.to = this.converToUnix(this.data.end);
            }
        },
        async init(params = null) {
            let self = this;
            self.isLoading = true;
            self.isError = false;
            self.results = [];
            const fromTimestamp = params ? params.from : self.data.from;
            const toTimestamp = params ? params.to : self.data.to;
            self.dateRange = self.getDateRange(fromTimestamp, toTimestamp);
            await ReportService.getReport(CREPORT.TYPE.INVENTORY_LOG, {
                from: fromTimestamp,
                to: toTimestamp,
                business_id: this.$store.state.setting.business_id,
                user_id: this.$root.currentActor.id,
                report_id: CREPORT.TYPE.INVENTORY_LOG,
                item_class:
                    self.data.itemClass != null ? self.data.itemClass : 0,
                items: self.data.items
                    ? self.data.items.map(item => item.raw_id)
                    : [],
                category: self.data.category,
                status: self.data.status,
                refresh: self.refresh
            })
                .then(res => {
                    if (res && res.message && res.message.code === 200) {
                        self.title = this.capitalize(res.resource.meta.title);
                        self.dynamicPageTitle(self.title);
                        this.results = res.resource.results;
                    } else if (res.queue) {
                        self._message(res.message, "warning");
                    } else {
                        throw new Error("Error: Unable to get the report.");
                    }
                })
                .catch(e => {
                    console.error(e);
                    self._message("Error: Unable to get the report", "danger");
                    self.isError = true;
                })
                .finally(() => {
                    self.isLoading = false;
                });
        }
    }
};
</script>
